<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto overflow-x-hidden"
    >
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-2 sm:pt-4 lg:pt-6 lg:max-w-7xl lg:mx-auto"
        >
            <div class="px-4 sm:px-6 lg:px-8 pb-12">
                <div>
                    <router-link
                        :to="{
                            name: 'ProjectReportsScreen',
                            params: {
                                projectKey: current.projectKey,
                                environmentKey: current.environmentKey,
                            },
                        }"
                        class="inline-flex items-center text-sm font-medium text-taupe-500 hover:text-taupe-800"
                    >
                        Back to Reports
                    </router-link>
                </div>

                <div class="sm:flex sm:items-center mt-2">
                    <div class="sm:flex sm:space-x-4">
                        <h1
                            id="primary-heading"
                            class="text-2xl font-medium text-taupe-800"
                        >
                            Reports
                        </h1>
                    </div>
                </div>

                <div class="mt-2 sm:mt-4 space-y-4">
                    <div class="sm:flex sm:items-center">
                        <div class="sm:flex">
                            <h2 class="text-xl font-medium text-taupe-800">
                                User Activity
                            </h2>
                        </div>
                    </div>

                    <!-- Denied state -->
                    <zen-denied-state
                        v-if="
                            !canViewUserActivity &&
                            !hasPlanFeature('audit_log_access')
                        "
                        title="User activity access restricted"
                        message="User activity needs a higher plan — no sales call, just upgrade in Billing."
                    />
                    <zen-denied-state
                        v-else-if="!canViewUserActivity"
                        title="User activity access restricted"
                        message="You don't have permission to view user activity. Contact your account administrator to request access."
                    />

                    <template v-if="canViewUserActivity">
                        <!-- Filters -->
                        <div
                            class="grid grid-cols-2 gap-3 sm:flex sm:items-end sm:gap-4 rounded-md border border-taupe-200 bg-taupe-50 p-3"
                        >
                            <div>
                                <label
                                    for="user-activity-filter-user"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    User
                                </label>
                                <select
                                    id="user-activity-filter-user"
                                    v-model="filters.actor_ulid"
                                    data-testid="user-activity-filter-user"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                >
                                    <option value="">All users</option>
                                    <option
                                        v-for="actor in actorOptions"
                                        :key="actor.ulid"
                                        :value="actor.ulid"
                                    >
                                        {{ actor.display_name }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <label
                                    for="user-activity-filter-team"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    Team
                                </label>
                                <select
                                    id="user-activity-filter-team"
                                    v-model="filters.team_ulid"
                                    data-testid="user-activity-filter-team"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                >
                                    <option value="">All teams</option>
                                    <option
                                        v-for="team in teams"
                                        :key="team.ulid"
                                        :value="team.ulid"
                                    >
                                        {{ team.name }}
                                    </option>
                                </select>
                            </div>
                            <div>
                                <label
                                    for="user-activity-filter-from"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    From
                                </label>
                                <input
                                    id="user-activity-filter-from"
                                    type="date"
                                    v-model="filters.created_from"
                                    data-testid="user-activity-filter-from"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                />
                            </div>
                            <div>
                                <label
                                    for="user-activity-filter-to"
                                    class="block text-sm font-medium text-taupe-700 mb-1"
                                >
                                    To
                                </label>
                                <input
                                    id="user-activity-filter-to"
                                    type="date"
                                    v-model="filters.created_to"
                                    data-testid="user-activity-filter-to"
                                    class="block w-full rounded-md border-taupe-300 shadow-xs focus:border-sky-500 focus:ring-sky-500 text-sm"
                                />
                            </div>
                            <div>
                                <zen-button
                                    variant="secondary"
                                    size="field"
                                    data-testid="user-activity-apply-filters"
                                    :processing="loading"
                                    @click="applyFilters"
                                >
                                    Apply
                                </zen-button>
                            </div>
                        </div>

                        <zen-loading-indicator v-if="loading" />

                        <zen-message-block v-if="error" type="error">
                            {{
                                error.message ||
                                'Failed to load user activity. Please try again.'
                            }}
                        </zen-message-block>

                        <template v-if="!loading && !error">
                            <!-- Summary -->
                            <dl
                                class="grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-taupe-50 ring-1 ring-taupe-200 sm:grid-cols-4"
                                data-testid="user-activity-summary"
                            >
                                <div class="bg-white px-4 py-4 sm:px-5 sm:py-4">
                                    <dt
                                        class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                                    >
                                        Active Users
                                    </dt>
                                    <dd
                                        class="mt-1.5 text-xl font-medium text-taupe-800"
                                        data-testid="user-activity-summary-total-actors"
                                    >
                                        {{ summary.total_actors ?? 0 }}
                                    </dd>
                                </div>
                                <div class="bg-white px-4 py-4 sm:px-5 sm:py-4">
                                    <dt
                                        class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                                    >
                                        Total Actions
                                    </dt>
                                    <dd
                                        class="mt-1.5 text-xl font-medium text-taupe-800"
                                        data-testid="user-activity-summary-total-actions"
                                    >
                                        {{ summary.total_actions ?? 0 }}
                                    </dd>
                                </div>
                                <div class="bg-white px-4 py-4 sm:px-5 sm:py-4">
                                    <dt
                                        class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                                    >
                                        Avg. Actions / User
                                    </dt>
                                    <dd
                                        class="mt-1.5 text-xl font-medium text-taupe-800"
                                        data-testid="user-activity-summary-average"
                                    >
                                        {{
                                            summary.average_actions_per_actor ??
                                            0
                                        }}
                                    </dd>
                                </div>
                                <div class="bg-white px-4 py-4 sm:px-5 sm:py-4">
                                    <dt
                                        class="text-xs font-medium uppercase tracking-wider text-taupe-400"
                                    >
                                        Outliers
                                    </dt>
                                    <dd
                                        class="mt-1.5 text-xl font-medium text-taupe-800"
                                        data-testid="user-activity-summary-outliers"
                                    >
                                        {{ outlierCount }}
                                    </dd>
                                </div>
                            </dl>

                            <!-- Table -->
                            <div
                                class="-mx-4 mt-8 overflow-hidden shadow-sm sm:-mx-6 md:mx-0 md:rounded-lg"
                            >
                                <table
                                    class="min-w-full divide-y divide-taupe-300"
                                >
                                    <thead class="bg-taupe-50">
                                        <tr>
                                            <th
                                                scope="col"
                                                class="w-full lg:w-2/5 py-3.5 pl-4 pr-3 text-left text-sm lg:text-base font-medium text-taupe-800 sm:pl-6"
                                            >
                                                <button
                                                    type="button"
                                                    class="flex items-center space-x-1 hover:text-sky-500"
                                                    @click="toggleSort('name')"
                                                >
                                                    <span>User</span>
                                                    <span
                                                        v-if="
                                                            filters.sort_by ===
                                                            'name'
                                                        "
                                                        >{{
                                                            sortIndicator
                                                        }}</span
                                                    >
                                                </button>
                                            </th>
                                            <th
                                                scope="col"
                                                class="hidden px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800 lg:table-cell"
                                            >
                                                Create
                                            </th>
                                            <th
                                                scope="col"
                                                class="hidden px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800 lg:table-cell"
                                            >
                                                Update
                                            </th>
                                            <th
                                                scope="col"
                                                class="hidden px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800 lg:table-cell"
                                            >
                                                Delete
                                            </th>
                                            <th
                                                scope="col"
                                                class="px-3 py-3.5 text-left text-sm lg:text-base font-medium text-taupe-800"
                                            >
                                                <button
                                                    type="button"
                                                    class="flex items-center space-x-1 hover:text-sky-500"
                                                    @click="
                                                        toggleSort(
                                                            'total_actions'
                                                        )
                                                    "
                                                >
                                                    <span>Total</span>
                                                    <span
                                                        v-if="
                                                            filters.sort_by ===
                                                            'total_actions'
                                                        "
                                                        >{{
                                                            sortIndicator
                                                        }}</span
                                                    >
                                                </button>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody
                                        class="divide-y divide-taupe-200 bg-white"
                                    >
                                        <tr
                                            v-for="actor in rows"
                                            :key="actor.actor_ulid"
                                            :data-testid="
                                                'user-activity-row-' +
                                                actor.actor_ulid
                                            "
                                        >
                                            <td
                                                class="w-full max-w-0 py-4 pl-4 pr-3 text-sm lg:text-base font-medium text-taupe-800 sm:w-auto sm:max-w-none sm:pl-6"
                                            >
                                                <div>
                                                    {{
                                                        actor.actor?.name ||
                                                        actor.actor?.email ||
                                                        actor.actor_ulid
                                                    }}
                                                    <span
                                                        v-if="
                                                            actor.activity_level !==
                                                            'normal'
                                                        "
                                                        class="ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                                                        :class="
                                                            activityBadgeClass(
                                                                actor.activity_level
                                                            )
                                                        "
                                                        :data-testid="
                                                            'user-activity-badge-' +
                                                            actor.actor_ulid
                                                        "
                                                    >
                                                        {{
                                                            actor.activity_level ===
                                                            'high'
                                                                ? 'Unusually high activity'
                                                                : 'Unusually low activity'
                                                        }}
                                                    </span>
                                                </div>
                                                <div
                                                    v-if="
                                                        actor.actor?.email &&
                                                        actor.actor?.name
                                                    "
                                                    class="font-light text-sm text-taupe-500"
                                                >
                                                    {{ actor.actor.email }}
                                                </div>
                                                <dl
                                                    class="font-light lg:hidden mt-1"
                                                >
                                                    <dt class="sr-only">
                                                        Breakdown
                                                    </dt>
                                                    <dd class="text-taupe-500">
                                                        {{
                                                            actor.by_category
                                                                .create
                                                        }}
                                                        created ·
                                                        {{
                                                            actor.by_category
                                                                .update
                                                        }}
                                                        updated ·
                                                        {{
                                                            actor.by_category
                                                                .delete
                                                        }}
                                                        deleted
                                                    </dd>
                                                </dl>
                                            </td>
                                            <td
                                                class="hidden px-3 py-4 text-sm lg:text-base text-taupe-800 lg:table-cell"
                                            >
                                                {{ actor.by_category.create }}
                                            </td>
                                            <td
                                                class="hidden px-3 py-4 text-sm lg:text-base text-taupe-800 lg:table-cell"
                                            >
                                                {{ actor.by_category.update }}
                                            </td>
                                            <td
                                                class="hidden px-3 py-4 text-sm lg:text-base text-taupe-800 lg:table-cell"
                                            >
                                                {{ actor.by_category.delete }}
                                            </td>
                                            <td
                                                class="px-3 py-4 text-sm lg:text-base font-medium text-taupe-800"
                                            >
                                                {{ actor.total_actions }}
                                            </td>
                                        </tr>
                                        <tr v-if="rows.length === 0">
                                            <td colspan="5">
                                                <zen-empty-table-message
                                                    compact
                                                    data-testid="user-activity-empty"
                                                    icon="ZenIconUser"
                                                >
                                                    No user activity in the
                                                    selected range.
                                                </zen-empty-table-message>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </template>

                        <div v-if="!loading" class="text-sm pt-6">
                            Report Generated:
                            <zen-date-time :value="generatedAt" />
                        </div>
                    </template>
                </div>
            </div>
        </section>
    </main>
</template>

<script>
import AuditService from '@/services/audit.service'
import UserService from '@/services/user.service'
import TeamService from '@/services/team.service'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { usePlan } from '@/composables/usePlan'
import { useCurrentEnvironment } from '@/composables/useCurrentEnvironment'
import { useCancelableLoad } from '@/composables/useCancelableLoad'

const ACTIVITY_BADGE_CLASSES = new Map([
    ['high', 'bg-purple-100 text-purple-800'],
    ['low', 'bg-taupe-100 text-taupe-600'],
])

export default {
    name: 'UserActivity',
    setup() {
        const { current } = useCurrentEnvironment()
        const { can } = usePermissions()
        const { hasPlanFeature } = usePlan()
        const { loading, error, run } = useCancelableLoad()

        return { current, can, hasPlanFeature, loading, error, run }
    },
    data() {
        return {
            summary: {},
            rows: [],
            actorOptions: [],
            teams: [],
            generatedAt: null,
            filters: {
                actor_ulid: '',
                team_ulid: '',
                created_from: '',
                created_to: '',
                sort_by: 'total_actions',
                sort_dir: 'desc',
            },
        }
    },
    computed: {
        canViewUserActivity() {
            return (
                this.can(PERMISSIONS.AUDIT_LOG_READ) &&
                this.hasPlanFeature('audit_log_access')
            )
        },
        environmentScopeKey() {
            return `${this.current.projectKey}:${this.current.environmentKey}`
        },
        sortIndicator() {
            return this.filters.sort_dir === 'asc' ? '▲' : '▼'
        },
        outlierCount() {
            return (
                (this.summary.high_activity_actor_ulids?.length ?? 0) +
                (this.summary.low_activity_actor_ulids?.length ?? 0)
            )
        },
    },
    watch: {
        environmentScopeKey() {
            this.refresh()
        },
    },
    methods: {
        activityBadgeClass(level) {
            return ACTIVITY_BADGE_CLASSES.get(level) ?? ''
        },
        toggleSort(sortBy) {
            if (this.filters.sort_by === sortBy) {
                this.filters.sort_dir =
                    this.filters.sort_dir === 'asc' ? 'desc' : 'asc'
            } else {
                this.filters.sort_by = sortBy
                this.filters.sort_dir = 'desc'
            }

            this.load()
        },
        applyFilters() {
            this.load()
        },
        load() {
            if (!this.canViewUserActivity) return

            this.run(
                (signal) =>
                    AuditService.getUserActivity(
                        {
                            actor_ulid: this.filters.actor_ulid || undefined,
                            team_ulid: this.filters.team_ulid || undefined,
                            created_from:
                                this.filters.created_from || undefined,
                            created_to: this.filters.created_to
                                ? `${this.filters.created_to}T23:59:59Z`
                                : undefined,
                            sort_by: this.filters.sort_by,
                            sort_dir: this.filters.sort_dir,
                        },
                        { signal }
                    ),
                (response) => {
                    this.summary = response.data.summary
                    this.rows = response.data.actors
                    this.generatedAt = response.data.generated_at
                },
                () => {
                    this.summary = {}
                    this.rows = []
                }
            )
        },
        refresh() {
            this.load()

            if (!this.canViewUserActivity) return

            UserService.list()
                .then((response) => {
                    this.actorOptions = response.data ?? []
                })
                .catch(() => {
                    this.actorOptions = []
                })

            TeamService.list()
                .then((response) => {
                    this.teams = response.data ?? []
                })
                .catch(() => {
                    this.teams = []
                })
        },
    },
    mounted() {
        this.refresh()
    },
}
</script>
